import React, { useCallback, useMemo } from 'react'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { EmptyArray, groupBy, max, min, sortByAll } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getAvailablePackagesByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import MobileMultipleRoomsChangeRoomOption from './MobileMultipleRoomsChangeRoomOption'
import { MultipleRoomPackage } from 'contexts/OfferPage/offerPageStateReducer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

interface Props {
  checkIn?: string;
  rooms: Array<App.RoomOccupants>;
  packageOptions: Array<OfferPackageOptionView>;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  room: App.RoomOccupants;
  roomIndex: number;
  selectedPackageView: OfferPackageOptionView;
  multipleRoomPackages: Array<MultipleRoomPackage>;
}

function MobileMultipleRoomsChangeRoomModal(props: Props) {
  const { room, roomIndex, selectedPackageView, checkIn, rooms, packageOptions, availablePackagesByRoom, multipleRoomPackages } = props

  const modalElement = useModalElementContext<App.HotelPackage | undefined>()

  const onRoomSelect = useCallback((packageOption: App.HotelPackage) => {
    modalElement.resolve(packageOption)
    showSnackbar(`Room ${roomIndex + 1} has been updated`, 'success', { action: { label: 'OK' } })
  }, [modalElement, roomIndex])

  const closeModal = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageOptions, checkIn])

  const availablePackages = availablePackagesByRoom[room.roomId]
  const optionsByRoomType = useMemo(() => {
    const rawOptions = Array.from(groupBy(availablePackages, (pkg) => pkg?.package.roomType?.id).values())
    return sortByAll(rawOptions, [
      // First order by room that has a package selection (if selection is to be shown)
      { selector: options => multipleRoomPackages.some(selectedRoom => selectedRoom.roomTypeId === options[0].package.roomType?.id) ? 1 : 0, direction: 'desc' },
      // Then order by price
      { selector: options => min(options, option => option.hotelTotal), direction: 'asc' },
      // Then order by availability of rooms in the room type from having the most to the least
      { selector: options => (options[0].availableRooms ?? 0) - rooms.length, direction: 'desc' },
    ])
  },
  [availablePackages, multipleRoomPackages, rooms.length])

  return (
    <ModalBase mode="drawer">
      <ModalHeader
        title={`Room ${roomIndex + 1} of ${rooms.length}`}
        subtitle={formatOccupantsShort(room)}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <Pane background="neutral-six">
          <Group direction="vertical" gap={8}>
            {optionsByRoomType.map((packageOptions, index) => <MobileMultipleRoomsChangeRoomOption
              key={index}
              packageOptions={packageOptions}
              availableRoomCount={availableRoomCountByRoomId.get(packageOptions[0].package.roomType?.id)}
              selectedPackageView={selectedPackageView}
              onRoomSelect={onRoomSelect}
            />)}
          </Group>
        </Pane>
      </ModalBody>
    </ModalBase>
  )
}

type MappedProps = Pick<Props,
  'checkIn'
  | 'rooms'
  | 'packageOptions'
  | 'availablePackagesByRoom'
  | 'multipleRoomPackages'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkIn: state.checkInDate,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  packageOptions: getDurationPackageOptions(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  multipleRoomPackages: state.multipleRoomPackages,
}), MobileMultipleRoomsChangeRoomModal)
